// Admin scraper API DTOs + fetch entry points.
//
// Mirrors the backend contract served by `/api/v1/admin/scraper/*` (DEV-214)
// and the `/api/v1/signals` feed used to source the cross-site divergence panel.
// In mock mode (VITE_USE_MOCK !== '0') we delegate to ./adminScraperMocks.ts so
// the dashboard renders without a running backend during local development.

import { isMockMode } from './client';
import * as mocks from './adminScraperMocks';

// Mirror the RunStatus union owned by the StatusPill component. Imported here
// (rather than redeclared) so adding a status anywhere flows back through
// the dashboard automatically.
import type { RunStatus } from '$lib/components/ui/StatusPill';

export type RunTrigger = 'manual' | 'scheduled' | 'api';
export type StageId = 'scrape' | 'normalize' | 'diff' | 'persist' | 'enrich';

// Backend exposes `completed` for the run status; the FE pill distinguishes
// `done` and `done-with-errors` based on `error_count`. This helper keeps the
// translation in one place — every consumer of a run goes through it.
export function uiRunStatus(status: string, errorCount: number): RunStatus {
  if (status === 'completed') {
    return errorCount > 0 ? 'done-with-errors' : 'done';
  }
  if (status === 'pending' || status === 'running' || status === 'paused' || status === 'failed' || status === 'cancelled') {
    return status;
  }
  // Unknown status — treat as pending so the pill never throws on a future
  // backend value the FE doesn't recognise.
  return 'pending';
}

// ---------- /pipeline/health ----------

export interface PipelineHealthPortal {
  slug: string;
  name: string;
  // Server-rendered "headline" string per portal (DEV-214 §3a). Used as the
  // accessible description on the portal card.
  headline: string;
  last_run_status: string | null;
  last_run_at: string | null;
  active_listings: number;
  error_count_last_run: number;
}

export interface PipelineHealthResponse {
  portals: PipelineHealthPortal[];
}

// ---------- /runs ----------

export interface ScrapeRunSummary {
  id: string;
  portal_slug: string;
  status: string;
  trigger: RunTrigger;
  current_stage: StageId | null;
  target_count: number | null;
  error_count: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ScrapeRunDetail extends ScrapeRunSummary {
  // Backend ships the per-stage counters as an opaque JSONB blob. Keys
  // documented in admin.progress.* — readers narrow on `[stage]` then
  // pluck named keys.
  stage_progress: Record<string, Record<string, number>> | null;
  stage_checkpoints: Record<string, unknown> | null;
  error_log: Array<Record<string, unknown>> | null;
  pipeline_result: Record<string, unknown> | null;
  paused_at: string | null;
  updated_at: string;
}

export interface PaginatedRuns {
  items: ScrapeRunSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface RunsQuery {
  status?: string[]; // comma-joined server-side
  portal?: string;
  days?: number;
  page?: number;
  page_size?: number;
}

// ---------- /signals (divergence panel) ----------

export type DivergenceEventType = 'cross_site_price_divergence' | 'cross_site_new_listing';

// Defensive shape — backend's `payload` is `Record<string, Any>`. We expect
// price-divergence events to carry per-portal rents and a spread, but the
// renderer reads each field with optional chaining so a payload-shape change
// downgrades the row gracefully instead of crashing the panel.
export interface DivergencePayload {
  portals?: Array<{ portal: string; rent: number | null }>;
  spread_pct?: number;
  // Spread direction sign — server may omit; FE falls back to spread_pct sign.
  sign?: '+' | '-';
}

export interface SignalEventDTO {
  id: string;
  eventType: string;
  createdAt: string;
  unit: { id: string; address: string; city: string | null; postnummer: string | null } | null;
  listing: { id: string; url: string; portal: string | null } | null;
  payload: DivergencePayload | null;
  summary: string;
}

export interface SignalFeedResponse {
  items: SignalEventDTO[];
  nextCursor: string | null;
  hasMore: boolean;
  total?: number | null;
}

// ---------- Fetchers ----------

export async function fetchPipelineHealth(): Promise<PipelineHealthResponse> {
  if (isMockMode()) return mocks.fetchPipelineHealth();
  const res = await fetch('/api/v1/admin/scraper/pipeline/health');
  if (!res.ok) throw new Error(`pipeline_health_${res.status}`);
  return (await res.json()) as PipelineHealthResponse;
}

export async function fetchRuns(query: RunsQuery = {}): Promise<PaginatedRuns> {
  if (isMockMode()) return mocks.fetchRuns(query);
  const params = new URLSearchParams();
  if (query.status?.length) params.set('status', query.status.join(','));
  if (query.portal) params.set('portal', query.portal);
  if (query.days != null) params.set('days', String(query.days));
  if (query.page != null) params.set('page', String(query.page));
  if (query.page_size != null) params.set('page_size', String(query.page_size));
  const res = await fetch(`/api/v1/admin/scraper/runs?${params.toString()}`);
  if (!res.ok) throw new Error(`runs_${res.status}`);
  return (await res.json()) as PaginatedRuns;
}

export async function fetchRunDetail(runId: string): Promise<ScrapeRunDetail | null> {
  if (isMockMode()) return mocks.fetchRunDetail(runId);
  const res = await fetch(`/api/v1/admin/scraper/runs/${encodeURIComponent(runId)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`run_detail_${res.status}`);
  return (await res.json()) as ScrapeRunDetail;
}

export async function startRun(portalSlug: string, targetCount?: number): Promise<{ id: string; status: string }> {
  if (isMockMode()) return mocks.startRun(portalSlug, targetCount);
  const res = await fetch('/api/v1/admin/scraper/runs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ portal_slug: portalSlug, target_count: targetCount })
  });
  if (!res.ok) throw new Error(`start_run_${res.status}`);
  return (await res.json()) as { id: string; status: string };
}

export async function retryRun(
  runId: string,
  stage?: StageId
): Promise<{ status: string; retry_from_stage?: string }> {
  if (isMockMode()) return mocks.retryRun(runId, stage);
  const res = await fetch(`/api/v1/admin/scraper/runs/${encodeURIComponent(runId)}/retry`, {
    method: 'POST',
    headers: stage ? { 'Content-Type': 'application/json' } : undefined,
    body: stage ? JSON.stringify({ stage }) : undefined
  });
  if (!res.ok) throw new Error(`retry_run_${res.status}`);
  return (await res.json()) as { status: string; retry_from_stage?: string };
}

export async function cancelRun(runId: string): Promise<{ status: string }> {
  if (isMockMode()) return mocks.cancelRun(runId);
  const res = await fetch(`/api/v1/admin/scraper/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST'
  });
  if (!res.ok) throw new Error(`cancel_run_${res.status}`);
  return (await res.json()) as { status: string };
}

export async function fetchDivergenceSignals(limit = 10): Promise<SignalFeedResponse> {
  if (isMockMode()) return mocks.fetchDivergenceSignals(limit);
  const params = new URLSearchParams();
  params.set('eventType', 'cross_site_price_divergence,cross_site_new_listing');
  params.set('pageSize', String(limit));
  const res = await fetch(`/api/v1/signals?${params.toString()}`);
  if (!res.ok) throw new Error(`signals_${res.status}`);
  return (await res.json()) as SignalFeedResponse;
}
